import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import {
  MapPin,
  Package,
  List,
  Store,
  Heart,
  Users,
  ShoppingBag,
  MessageCircle,
  Sprout,
  User,
  Home,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SellerMap } from "@/components/SellerMap";

function readBuyerCenter(): { latitude: number; longitude: number } | null {
  try {
    const raw = window.localStorage.getItem("marketlink_buyer_location");
    if (!raw) return null;
    const p = JSON.parse(raw)?.point;
    if (p && typeof p.latitude === "number" && typeof p.longitude === "number") return p;
    return null;
  } catch {
    return null;
  }
}

// The page/tab the map was opened from, so the toggle can show where tapping
// returns you. On Home the active browse tab lives in `?mode=`; elsewhere it's
// the top-level route.
function originFor(path: string, search: string): { icon: LucideIcon; label: string } {
  if (path === "/") {
    const mode = new URLSearchParams(search).get("mode");
    switch (mode) {
      case "products":
        return { icon: List, label: "Listings" };
      case "producers":
        return { icon: Store, label: "Makers" };
      case "following":
        return { icon: Users, label: "Following" };
      case "saved":
        return { icon: Heart, label: "Saved" };
      case "boxes":
        return { icon: Package, label: "Boxes" };
      default:
        return { icon: Home, label: "Browse" };
    }
  }
  if (
    path.startsWith("/orders") ||
    path.startsWith("/deliveries") ||
    path.startsWith("/checkout") ||
    path.startsWith("/order-confirmation")
  ) {
    return { icon: ShoppingBag, label: "Orders" };
  }
  if (path.startsWith("/messages")) return { icon: MessageCircle, label: "Messages" };
  if (path.startsWith("/garden")) return { icon: Sprout, label: "Garden" };
  if (path.startsWith("/account") || path.startsWith("/seller")) return { icon: User, label: "Account" };
  return { icon: Home, label: "Back" };
}

// Global map toggle: available on every tab, in one fixed spot (bottom-right,
// above the nav, clear of the center List FAB). Tapping opens the sellers map
// as a full-screen overlay; the button stays put and swaps to the icon+label of
// the page you came from, so tapping again returns you there.
export function MapToggle() {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const search = useSearch();
  const [showMap, setShowMap] = useState(false);
  const [center, setCenter] = useState<{ latitude: number; longitude: number } | null>(null);
  // Height of the maker quick-view sheet, reported by SellerMap. 0 when nothing
  // is selected, which puts the button back in its normal resting spot.
  const [sheetHeight, setSheetHeight] = useState(0);
  const [focusSellerId, setFocusSellerId] = useState<string | null>(null);
  const [origin, setOrigin] = useState<{ icon: LucideIcon; label: string }>({
    icon: Home,
    label: "Back",
  });

  // The open map is a history entry (we pushState on open), so the system/back
  // arrow returns to the map instead of the page underneath it. Tapping a maker
  // or listing pushes that destination on top of the map entry, so Back from
  // there pops to the map entry and reopens it — even when you opened the map
  // from another maker's profile. Visibility is derived from history state:
  // the [location] effect covers cross-URL navigation; the popstate listener
  // covers same-URL back/forward (the map entry shares its base page's URL, so
  // wouter's location doesn't change when you move in or out of it).
  useEffect(() => {
    setShowMap(Boolean(window.history.state?.stoopMap));
  }, [location]);
  useEffect(() => {
    const onPop = () => setShowMap(Boolean(window.history.state?.stoopMap));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // SellerMap unmounts without reporting a final 0, so clear it here or the
  // button would stay lifted after the map closes.
  useEffect(() => {
    if (!showMap) setSheetHeight(0);
  }, [showMap]);

  if (!isAuthenticated) return null;

  // Pages that own a fixed bottom CTA (product detail's Add to cart, checkout's
  // pay bar): the bottom-right pill would sit on top of the critical action, so
  // keep it off there. (Closing the map on navigation is handled by the effect
  // above, not by this guard.)
  if (location.startsWith("/items/") || location.startsWith("/checkout")) {
    return null;
  }

  const Icon = showMap ? origin.icon : MapPin;
  const label = showMap ? origin.label : "Map";

  return (
    <>
      {showMap && (
        <SellerMap
          initialCenter={center}
          focusSellerId={focusSellerId}
          onNavigate={() => setShowMap(false)}
          onSheetHeightChange={setSheetHeight}
        />
      )}
      <button
        type="button"
        onClick={() => {
          if (showMap) {
            // Pop the pushed map entry; the popstate listener hides the map.
            window.history.back();
          } else {
            setOrigin(originFor(location, search));
            setCenter(readBuyerCenter());
            setFocusSellerId(location.match(/^\/u\/([^/?#]+)/)?.[1] ?? null);
            window.history.pushState({ ...window.history.state, stoopMap: true }, "");
            setShowMap(true);
          }
        }}
        className={`fixed right-4 inline-flex items-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background shadow-lg shadow-foreground/25 transition-[bottom] duration-200 active:scale-95 ${
          showMap ? "z-[62]" : "z-40"
        }`}
        // Always bottom-right, the same spot it holds on every other page, so it
        // doesn't jump when the map opens. When a maker pin is tapped the sheet
        // slides up from the bottom, so ride up above its measured height rather
        // than sitting underneath it.
        style={{
          bottom:
            showMap && sheetHeight > 0
              ? `${Math.round(sheetHeight) + 12}px`
              : "calc(env(safe-area-inset-bottom, 0px) + 64px)",
        }}
        data-testid="button-toggle-map"
        aria-label={showMap ? `Back to ${label}` : "Open map"}
      >
        <Icon className="h-4 w-4" />
        {label}
      </button>
    </>
  );
}
